import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import {
  CourierPackage,
  CourierUser,
  WarehousePackage,
} from '@shared/sdk-v1/models';
import { AuthorizedUser } from '@shared/sdk-v1/models/AuthorizedUser';

export interface TransformedCustomerData {
  id?: number;
  name: string; // Computed field: firstName + lastName (always present in cache)
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mailboxId: number; // Always present as number
  mailboxNumber: string; // Always present as formatted string with prefix
  rawMailboxNumber?: number; // Original mailbox number without prefix
  points: number;
  rewardsAccountId?: number | null;
  createdOn: Date | string;
  storeName?: string;
  storeLocationId?: number;
  trnNumber?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  isBusiness?: boolean;
  subscribedToCommunication?: boolean;
  gender?: string;
  dateOfBirth?: string;
  insuranceOptIn?: boolean;
}

interface CacheState {
  packages: {
    data: CourierPackage[];
    lastFetched: number | null;
  };
  dashboard: {
    data: any; // Replace with proper dashboard data type
    lastFetched: number | null;
  };
  users: {
    data: CourierUser[];
    lastFetched: number | null;
  };
  authorizedUsers: {
    data: AuthorizedUser[];
    lastFetched: number | null;
  };
  warehouse: {
    data: WarehousePackage[];
    lastFetched: number | null;
  };
}

// Helper functions for localStorage persistence
const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`Failed to load ${key} from localStorage:`, error);
    return defaultValue;
  }
};

const saveToLocalStorage = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
  }
};

// Initialize state from localStorage where available (following authSlice pattern)
const initialState: CacheState = {
  packages: loadFromLocalStorage('packages_cache', {
    data: [],
    lastFetched: null,
  }),
  dashboard: loadFromLocalStorage('dashboard_cache', {
    data: null,
    lastFetched: null,
  }),
  users: loadFromLocalStorage('users_cache', {
    data: [],
    lastFetched: null,
  }),
  authorizedUsers: loadFromLocalStorage('authorized_users_cache', {
    data: [],
    lastFetched: null,
  }),
  warehouse: loadFromLocalStorage('warehouse_cache', {
    data: [],
    lastFetched: null,
  }),
};

export const cacheSlice = createSlice({
  name: 'cache',
  initialState,
  reducers: {
    updatePackagesCache: (state, action: PayloadAction<CourierPackage[]>) => {
      const updatedPackages = {
        data: action.payload,
        lastFetched: Date.now(),
      };
      state.packages = updatedPackages;
      saveToLocalStorage('packages_cache', updatedPackages);
    },
    updateDashboardCache: (state, action: PayloadAction<any>) => {
      const updatedDashboard = {
        data: action.payload,
        lastFetched: Date.now(),
      };
      state.dashboard = updatedDashboard;
      saveToLocalStorage('dashboard_cache', updatedDashboard);
    },
    updateUsersCache: (state, action: PayloadAction<CourierUser[]>) => {
      const updatedUsers = {
        data: action.payload,
        lastFetched: Date.now(),
      };
      state.users = updatedUsers;
      saveToLocalStorage('users_cache', updatedUsers);
    },
    updateAuthorizedUsersCache: (
      state,
      action: PayloadAction<AuthorizedUser[]>
    ) => {
      const updatedAuthorizedUsers = {
        data: action.payload,
        lastFetched: Date.now(),
      };
      state.authorizedUsers = updatedAuthorizedUsers;
      saveToLocalStorage('authorized_users_cache', updatedAuthorizedUsers);
    },
    updateWarehouseCache: (
      state,
      action: PayloadAction<WarehousePackage[]>
    ) => {
      const updatedWarehouse = {
        data: action.payload,
        lastFetched: Date.now(),
      };
      state.warehouse = updatedWarehouse;
      saveToLocalStorage('warehouse_cache', updatedWarehouse);
      console.info(
        `📦 Warehouse cache updated: ${action.payload.length} packages, timestamp: ${updatedWarehouse.lastFetched}`
      );
    },
    clearImpersonationCache: (state) => {
      // Clear only the cache types that are affected by impersonation
      state.packages = { data: [], lastFetched: null };
      state.users = { data: [], lastFetched: null };
      state.authorizedUsers = { data: [], lastFetched: null };
      state.warehouse = { data: [], lastFetched: null };

      // Clear from localStorage
      localStorage.removeItem('packages_cache');
      localStorage.removeItem('customers_cache');
      localStorage.removeItem('users_cache');
      localStorage.removeItem('authorized_users_cache');
      localStorage.removeItem('warehouse_cache');
      console.info(
        '🔄 Impersonation-related cache cleared (packages, customers, users, authorized users, warehouse)'
      );
    },
    clearCache: (state) => {
      // Clear both Redux state and localStorage
      Object.assign(state, {
        packages: { data: [], lastFetched: null },
        customers: { data: [], lastFetched: null },
        dashboard: { data: null, lastFetched: null },
        users: { data: [], lastFetched: null },
        authorizedUsers: { data: [], lastFetched: null },
        warehouse: { data: [], lastFetched: null },
      });

      // Clear from localStorage
      localStorage.removeItem('packages_cache');
      localStorage.removeItem('customers_cache');
      localStorage.removeItem('dashboard_cache');
      localStorage.removeItem('users_cache');
      localStorage.removeItem('authorized_users_cache');
      localStorage.removeItem('warehouse_cache');
      console.info('🗑️ All cache cleared from Redux and localStorage');
    },
  },
});

// Export actions
export const {
  updatePackagesCache,
  updateDashboardCache,
  updateUsersCache,
  updateAuthorizedUsersCache,
  updateWarehouseCache,
  clearImpersonationCache,
  clearCache,
} = cacheSlice.actions;

// Export selectors with safe fallbacks
export const getPackagesCache = (state: RootState) =>
  state.cache?.packages || initialState.packages;
export const getDashboardCache = (state: RootState) =>
  state.cache?.dashboard || initialState.dashboard;
export const getUsersCache = (state: RootState) =>
  state.cache?.users || initialState.users;
export const getAuthorizedUsersCache = (state: RootState) =>
  state.cache?.authorizedUsers || initialState.authorizedUsers;
export const getWarehouseCache = (state: RootState) =>
  state.cache?.warehouse || initialState.warehouse;

export default cacheSlice.reducer;
